import { useState, useEffect } from 'react';

interface BrokerRow {
  initial: string;
  name: string;
  model: string;
  modelDesc: string;
  spreadPips: string;
  spreadCost: string;
  commission: string;
  commissionNote: string;
  swap: string;
  swapNote: string;
  total: string;
  totalValue: number;
  verdict: string;
  verdictStyle: 'danger' | 'neutral' | 'winner';
  breakdown: { spread: number; commission: number; swap: number };
  insight: string;
}

const brokers: BrokerRow[] = [
  {
    initial: 'A',
    name: 'Broker A',
    model: '0.0 pip spread',
    modelDesc: 'Raw-looking headline, fully baked-in costs elsewhere',
    spreadPips: '0.0',
    spreadCost: '$0.00',
    commission: '$7.00',
    commissionNote: 'per side',
    swap: '$2.50',
    swapNote: 'avg / day',
    total: '$16.50',
    totalValue: 16.5,
    verdict: 'Most Expensive',
    verdictStyle: 'danger',
    breakdown: { spread: 0, commission: 85, swap: 15 },
    insight: 'The 0.0 pip headline hides a $16.50 reality — you pay through commission instead',
  },
  {
    initial: 'B',
    name: 'Broker B',
    model: 'Commission-free',
    modelDesc: 'No extra fee, but the spread itself is wider',
    spreadPips: '1.2',
    spreadCost: '$12.00',
    commission: '$0.00',
    commissionNote: 'commission-free',
    swap: '$1.80',
    swapNote: 'avg / day',
    total: '$13.80',
    totalValue: 13.8,
    verdict: 'Middle Ground',
    verdictStyle: 'neutral',
    breakdown: { spread: 87, commission: 0, swap: 13 },
    insight: 'No commission sounds great, but the 1.2 pip spread adds up to $12 per lot',
  },
  {
    initial: 'C',
    name: 'Broker C',
    model: 'Raw ECN',
    modelDesc: 'Interbank price + transparent low commission',
    spreadPips: '0.1',
    spreadCost: '$1.00',
    commission: '$3.50',
    commissionNote: 'per side',
    swap: '$1.20',
    swapNote: 'avg / day',
    total: '$8.20',
    totalValue: 8.2,
    verdict: 'Best Value',
    verdictStyle: 'winner',
    breakdown: { spread: 12, commission: 74, swap: 14 },
    insight: 'Tiny spread + low commission = half the cost of the "0.0 pip" broker',
  },
];

const verdictConfig = {
  danger: {
    badge: 'bg-secondary-100 text-secondary-700',
    dot: 'bg-secondary-500',
    barSpread: 'bg-secondary-300',
    barCommission: 'bg-accent-500',
    barSwap: 'bg-foreground-400',
    cardBorder: 'border-background-200',
    cardBg: 'bg-white',
    initialBg: 'bg-secondary-50',
    initialText: 'text-secondary-600',
  },
  neutral: {
    badge: 'bg-primary-100 text-primary-700',
    dot: 'bg-primary-500',
    barSpread: 'bg-primary-500',
    barCommission: 'bg-accent-500',
    barSwap: 'bg-foreground-400',
    cardBorder: 'border-background-200',
    cardBg: 'bg-white',
    initialBg: 'bg-primary-50',
    initialText: 'text-primary-600',
  },
  winner: {
    badge: 'bg-accent-100 text-accent-700',
    dot: 'bg-accent-500',
    barSpread: 'bg-primary-500',
    barCommission: 'bg-accent-500',
    barSwap: 'bg-foreground-400',
    cardBorder: 'border-accent-300',
    cardBg: 'bg-accent-50/40',
    initialBg: 'bg-accent-100',
    initialText: 'text-accent-700',
  },
};

export default function SpreadVsTotalSection() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const maxTotal = Math.max(...brokers.map((b) => b.totalValue));

  return (
    <section id="spread-vs-total" className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[720px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[44px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            Low spread is a headline.
            <br />
            Total cost is the bill.
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Every broker advertises spread. Few advertise total cost. A <strong className="font-semibold text-foreground-800">0.0-pip spread</strong> sounds
            unbeatable — until you see the commission. A <strong className="font-semibold text-foreground-800">commission-free</strong> account sounds clean — until
            you compare the spread width. The only number that matters is what actually leaves your account per lot.
          </p>
        </div>

        {/* Three broker comparison cards */}
        <div className="space-y-4 md:space-y-5 mb-12 md:mb-16">
          {brokers.map((broker) => {
            const colors = verdictConfig[broker.verdictStyle];
            const barWidthPercent = (broker.totalValue / maxTotal) * 100;

            return (
              <div
                key={broker.name}
                className={`${colors.cardBg} rounded-card border ${colors.cardBorder} overflow-hidden hover:border-background-300 transition-colors duration-200`}
              >
                {/* Top row: identity + total cost */}
                <div className="p-5 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Left: Broker identity */}
                    <div className="flex items-start gap-4 min-w-0">
                      <div className={`w-11 h-11 md:w-12 md:h-12 rounded-[13px] ${colors.initialBg} flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-[18px] md:text-[20px] font-[800] ${colors.initialText}`}>
                          {broker.initial}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-[17px] md:text-[18px] font-bold text-foreground-950 leading-none mb-1">
                          {broker.name}
                        </h3>
                        <span className={`inline-flex items-center gap-1.5 text-[14px] font-semibold ${broker.verdictStyle === 'winner' ? 'text-accent-600' : broker.verdictStyle === 'danger' ? 'text-secondary-600' : 'text-primary-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} flex-shrink-0`} />
                          {broker.model}
                        </span>
                        <p className="text-[13px] text-foreground-500 mt-1.5 leading-relaxed max-w-[360px]">
                          {broker.modelDesc}
                        </p>
                      </div>
                    </div>

                    {/* Right: Total cost + verdict */}
                    <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-[12px] text-foreground-500 font-medium mb-0.5">Total / 1 lot, 1 day</div>
                        <div className={`text-[36px] md:text-[42px] font-[800] leading-none tracking-tight ${
                          broker.verdictStyle === 'winner' ? 'text-accent-600' : 'text-foreground-950'
                        }`}>
                          {broker.total}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-bold whitespace-nowrap ${colors.badge}`}>
                        {broker.verdictStyle === 'winner' && (
                          <i className="ri-star-fill text-[11px] mr-1 w-3 h-3 flex items-center justify-center" />
                        )}
                        {broker.verdict}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cost breakdown bar */}
                <div className="px-5 md:px-6 pb-5 md:pb-6">
                  <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                    Cost Breakdown
                  </div>

                  {/* Stacked bar */}
                  <div className="relative w-full h-[10px] bg-background-200 rounded-full overflow-hidden mb-3">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full overflow-hidden flex transition-all duration-1000 ease-out"
                      style={{ width: animated ? `${barWidthPercent}%` : '0%' }}
                    >
                      {broker.breakdown.spread > 0 && (
                        <div
                          className={`h-full ${colors.barSpread} transition-all duration-1000 ease-out`}
                          style={{ width: `${broker.breakdown.spread}%` }}
                        />
                      )}
                      {broker.breakdown.commission > 0 && (
                        <div
                          className={`h-full ${colors.barCommission} transition-all duration-1000 ease-out`}
                          style={{ width: `${broker.breakdown.commission}%` }}
                        />
                      )}
                      {broker.breakdown.swap > 0 && (
                        <div
                          className={`h-full ${colors.barSwap} transition-all duration-1000 ease-out`}
                          style={{ width: `${broker.breakdown.swap}%` }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Legend + dollar amounts */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-[3px] ${colors.barSpread} flex-shrink-0`} />
                      <span className="text-[13px] text-foreground-600">Spread</span>
                      <span className="text-[13px] font-bold text-foreground-900 ml-1">{broker.spreadCost}</span>
                      <span className="text-[12px] text-foreground-500">({broker.spreadPips} pips)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-[3px] ${colors.barCommission} flex-shrink-0`} />
                      <span className="text-[13px] text-foreground-600">Commission</span>
                      <span className="text-[13px] font-bold text-foreground-900 ml-1">{broker.commission}</span>
                      <span className="text-[12px] text-foreground-500">({broker.commissionNote})</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-[3px] ${colors.barSwap} flex-shrink-0`} />
                      <span className="text-[13px] text-foreground-600">Swap</span>
                      <span className="text-[13px] font-bold text-foreground-900 ml-1">{broker.swap}</span>
                      <span className="text-[12px] text-foreground-500">({broker.swapNote})</span>
                    </div>
                  </div>
                </div>

                {/* Insight line */}
                <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0 border-t border-background-200">
                  <div className="flex items-start gap-2 pt-4">
                    <i className="ri-lightbulb-line text-[15px] text-foreground-400 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0" />
                    <p className={`text-[13px] leading-relaxed ${
                      broker.verdictStyle === 'winner' ? 'text-accent-700 font-medium' : 'text-foreground-600'
                    }`}>
                      {broker.insight}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom: at-a-glance comparison + key takeaway */}
        <div className="bg-background-100 rounded-card border border-background-200 p-6 md:p-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left: narrative */}
            <div className="flex-1 max-w-[480px]">
              <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                The Pattern
              </div>
              <h4 className="text-[20px] md:text-[24px] font-[700] text-foreground-950 leading-snug mb-3">
                The broker with the lowest advertised spread is often the most expensive
              </h4>
              <p className="text-[14px] md:text-[15px] text-foreground-600 leading-relaxed mb-5">
                Broker A shouts <strong className="font-semibold text-foreground-800">0.0 pips</strong> from every landing page —
                but embeds the real cost in a $7/side commission. Broker B says <strong className="font-semibold text-foreground-800">&quot;commission-free&quot;</strong> but
                runs a 1.2 pip spread that costs $12 per lot. Broker C charges a tiny 0.1 pip spread plus a transparent $3.50 commission
                — and ends up <strong className="font-semibold text-accent-700">half the price</strong> of Broker A.
              </p>
              <p className="text-[14px] text-foreground-600 leading-relaxed">
                Always calculate total cost per lot: spread + commission + swap + expected slippage. The headline spread number is
                just one ingredient in a recipe that can either save or cost you thousands per month.
              </p>
            </div>

            {/* Right: visual comparison bars */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wide mb-4">
                Total Cost per Lot (1 day hold)
              </div>

              <div className="space-y-4">
                {brokers.map((broker) => {
                  const colors = verdictConfig[broker.verdictStyle];
                  const barWidthPercent = (broker.totalValue / maxTotal) * 100;

                  return (
                    <div key={`mini-${broker.name}`} className="flex items-center gap-3">
                      <span className="text-[14px] font-semibold text-foreground-800 w-[72px] flex-shrink-0">
                        {broker.name}
                      </span>
                      <div className="flex-1 h-[14px] bg-background-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ease-out ${
                            broker.verdictStyle === 'winner'
                              ? 'bg-accent-500'
                              : broker.verdictStyle === 'danger'
                                ? 'bg-secondary-400'
                                : 'bg-primary-400'
                          }`}
                          style={{ width: animated ? `${barWidthPercent}%` : '0%' }}
                        />
                      </div>
                      <span className={`text-[15px] font-[800] w-[56px] text-right flex-shrink-0 ${
                        broker.verdictStyle === 'winner' ? 'text-accent-600' : 'text-foreground-800'
                      }`}>
                        {broker.total}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Winner annotation */}
              <div className="mt-5 flex items-center gap-2.5 bg-accent-50 rounded-[10px] px-4 py-3 border border-accent-200">
                <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-[14px] text-white w-4 h-4 flex items-center justify-center" />
                </div>
                <div>
                  <span className="text-[13px] font-bold text-accent-700">
                    Broker C saves $8.30/lot vs Broker A
                  </span>
                  <span className="text-[13px] text-accent-600">
                    {' '}— that is $166/month for a single-lot daily trader
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div className="mt-7 pt-5 border-t border-background-200">
            <div className="flex items-start gap-2.5">
              <i className="ri-information-line text-[15px] text-foreground-500 mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0" />
              <p className="text-[13px] text-foreground-500 leading-relaxed">
                Costs shown for EURUSD, 1 standard lot ($10/pip), held 1 day. Actual costs vary by pair, account type, and holding
                period. Swap rates change daily based on central bank interest rate differentials. Commission structures differ
                between brokers — some charge per side, others per round-turn. Always check your broker&apos;s full fee schedule
                before opening a live account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}